import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import "../styles/my-registrations.css";
import "../styles/certificates.css";

const formatDate = (value) => {
  if (!value) return "Date to be confirmed";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
};

const formatTime = (value) => value ? new Date(`1970-01-01T${String(value).slice(0, 8)}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "Time to be confirmed";

export default function MyRegistrations() {
  const { token } = useContext(AuthContext);
  const [registrations, setRegistrations] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [certificateMessage, setCertificateMessage] = useState("");
  const [correctionCertificate, setCorrectionCertificate] = useState(null);
  const [correctionText, setCorrectionText] = useState("");
  const [sendingCorrection, setSendingCorrection] = useState(false);

  useEffect(() => {
    let active = true;
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/my-registrations`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => {
      if (active) { setRegistrations(data.registrations || []); setStatus("ready"); }
    }).catch((error) => {
      if (active) { setMessage(error.response?.data?.message || "Unable to load your registrations."); setStatus("error"); }
    });
    return () => { active = false; };
  }, [token]);

  const openCertificatePdf = async (certificate, download = false) => {
    setCertificateMessage("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/my-volunteer-certificates/${certificate.id}/pdf${download ? "?download=1" : ""}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      if (download) {
        const link = document.createElement("a");
        link.href = url;
        link.download = `${certificate.certificateNumber || "udyog-connect-certificate"}.pdf`;
        link.click();
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (error) {
      setCertificateMessage(error.response?.data?.message || "Unable to open this certificate. Please sign in again and retry.");
    }
  };

  const submitCorrectionRequest = async () => {
    if (!correctionCertificate || correctionText.trim().length < 10) {
      setCertificateMessage("Please describe the correction in at least 10 characters.");
      return;
    }
    setSendingCorrection(true);
    setCertificateMessage("");
    const idempotencyKey = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/my-volunteer-certificates/${correctionCertificate.id}/correction-requests`,
        { requestText: correctionText, idempotencyKey },
        { headers: { Authorization: `Bearer ${token}`, "Idempotency-Key": idempotencyKey } }
      );
      setCertificateMessage(data?.message || "Your correction request was sent to the UC certificate team.");
      setCorrectionCertificate(null);
      setCorrectionText("");
    } catch (error) {
      setCertificateMessage(error.response?.data?.message || "Unable to send your correction request.");
    } finally {
      setSendingCorrection(false);
    }
  };

  useEffect(() => {
    let active = true;
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/my-volunteer-certificates`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => {
      if (active) setCertificates(data.certificates || []);
    }).catch(() => {
      if (active) setCertificates([]);
    });
    return () => { active = false; };
  }, [token]);

  return (
    <section className="my-registrations-page">
      <div className="my-registrations-hero">
        <p className="my-registrations-eyebrow">YOUR UC SCHEDULE</p>
        <h1>My registrations</h1>
        <p>Keep your community meetups and UC events in one place. Confirmation emails remain available in your inbox.</p>
      </div>
      {status === "loading" && <div className="my-registrations-card">Loading your registrations…</div>}
      {status === "error" && <div className="my-registrations-card error">{message}</div>}
      {status === "ready" && registrations.length === 0 && (
        <div className="my-registrations-card empty">
          <h2>No registrations yet</h2>
          <p>Browse the calendar to find a session that fits you.</p>
          <Link className="my-registrations-button" to="/2.0/calendar">Browse calendar</Link>
        </div>
      )}
      <div className="my-registrations-list">
        {registrations.map((registration) => {
          const isMeetup = registration.registration_type === "community_meetup";
          const sharePath = isMeetup
            ? `/2.0/community-meetup/${encodeURIComponent(registration.slug)}/register`
            : `/2.0/event/${encodeURIComponent(registration.event_id)}/register`;
          return (
            <article className="my-registrations-card" key={`${registration.registration_type}-${registration.registration_id}`}>
              <div className="my-registration-card-top">
                <span className={`my-registration-kind ${isMeetup ? "meetup" : "event"}`}>{isMeetup ? "Community meetup" : (registration.event_category || "UC event")}</span>
                <span className={`my-registration-status ${registration.status}`}>{registration.status || "registered"}</span>
              </div>
              <h2>{registration.title}</h2>
              <p className="my-registration-date">{formatDate(registration.event_date)} · {formatTime(registration.start_time)}{registration.end_time ? ` – ${formatTime(registration.end_time)}` : ""}</p>
              <p className="my-registration-venue">{registration.venue_name || "Venue details will be shared by UC"}{registration.city ? ` · ${registration.city}${registration.state ? `, ${registration.state}` : ""}` : ""}</p>
              <div className="my-registration-meta">Registered {registration.registered_at ? new Date(registration.registered_at).toLocaleDateString() : ""}{registration.seats_requested > 1 ? ` · ${registration.seats_requested} seats` : ""}</div>
              <Link className="my-registration-link" to={sharePath}>View registration page</Link>
            </article>
          );
        })}
      </div>
      {status === "ready" && (
        <section className="my-certificates-section" aria-label="My service certificates">
          <div>
            <p className="my-registrations-eyebrow">MY VOLUNTEER RECORD</p>
            <h2>My Certificates</h2>
            <p>Certificates appear after UC confirms attendance and service hours. Only you and authorized UC administrators can open the PDF.</p>
          </div>
          {certificateMessage && <p className="my-certificate-message" role="status">{certificateMessage}</p>}
          {certificates.length === 0 ? (
            <article className="my-registrations-card empty"><p>No service certificates have been issued yet.</p></article>
          ) : (
            <div className="my-certificates-grid">
              {certificates.map((certificate) => {
                const snapshot = certificate.snapshot || {};
                const issued = certificate.status === "issued";
                return <article className="my-certificate-card" key={certificate.id}>
                  <div className="my-certificate-card-top">
                    <span className="my-registration-kind event">{snapshot.certificateTypeLabel || "Volunteer certificate"}</span>
                    <span className={`my-certificate-status ${certificate.status || "issued"}`}>{certificate.status || "issued"}</span>
                  </div>
                  <h3>{snapshot.eventName || "Udyog Connect volunteer service"}</h3>
                  <dl className="my-certificate-details">
                    <div><dt>Event date</dt><dd>{snapshot.serviceDate || "—"}</dd></div>
                    <div><dt>Approved service hours</dt><dd>{snapshot.approvedHours ?? "—"}</dd></div>
                    <div><dt>Issue date</dt><dd>{snapshot.issueDate || formatDate(certificate.issuedAt)}</dd></div>
                    <div><dt>Certificate number</dt><dd>{certificate.certificateNumber}</dd></div>
                  </dl>
                  <div className="my-certificate-actions">
                    <button type="button" onClick={() => openCertificatePdf(certificate)} disabled={!issued}>View certificate</button>
                    <button type="button" className="secondary" onClick={() => openCertificatePdf(certificate, true)} disabled={!issued}>Download PDF</button>
                    <Link className="verification-link" to={`/2.0/certificates/verify/${encodeURIComponent(certificate.publicId)}`}>Verification</Link>
                  </div>
                  <button type="button" className="my-certificate-correction" onClick={() => { setCorrectionCertificate(certificate); setCorrectionText(""); }}>Request a correction</button>
                </article>;
              })}
            </div>
          )}
        </section>
      )}
      {correctionCertificate && <div className="my-certificate-modal-backdrop" role="presentation" onMouseDown={() => !sendingCorrection && setCorrectionCertificate(null)}>
        <section className="my-certificate-modal" role="dialog" aria-modal="true" aria-labelledby="certificate-correction-title" onMouseDown={(event) => event.stopPropagation()}>
          <button type="button" className="my-certificate-modal-close" onClick={() => !sendingCorrection && setCorrectionCertificate(null)} aria-label="Close">×</button>
          <p className="my-registrations-eyebrow">CERTIFICATE CORRECTION</p>
          <h2 id="certificate-correction-title">Request a review</h2>
          <p>Tell the UC certificate team what needs correction. You cannot edit approved hours, event details, certificate number, or issued certificate content yourself.</p>
          <textarea value={correctionText} onChange={(event) => setCorrectionText(event.target.value)} placeholder="For example: My certificate display name should include my middle initial." maxLength={2000} rows={5} />
          <div className="my-certificate-modal-actions"><button type="button" className="secondary" onClick={() => setCorrectionCertificate(null)} disabled={sendingCorrection}>Cancel</button><button type="button" onClick={submitCorrectionRequest} disabled={sendingCorrection}>{sendingCorrection ? "Sending…" : "Send request"}</button></div>
        </section>
      </div>}
    </section>
  );
}
